import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { IManufactoringProduction } from "../models/manufactoringProduction";
import { IManufactoringProductionApiResponse } from "../models/api/manufactoringProductionApiResponse";
import { IManufactoringProductionForm } from "../models/forms/manufactoringProductionForm";
import { IManufactoringProductionFilter } from "../models/filters/manufactoringProductionFilter";
import ManufactoringProductionServices from "../services/ManufactoringProductionService";
import { usePerformanceTracker } from "../composables/tools/PerformanceComposable";
import { IProduction } from "../models/production";
import { ServiceOperationResult } from "../models/api/serviceOperationResult";
import DataOperationError from "../models/api/dataOperationError";
import { IDataOperationResult } from "../models/api/dataOperationResult";
import { useProductionStore } from "./production.store";
import signalRManager from "../helper/signalRManager";
import i18n from "@/i18n";
import IPayloadStore from "../models/api/payloadStore";

export const useManufactoringProductionStore = defineStore("ManufactoringProduction", () => {
    // State
    const datas = ref<IManufactoringProduction[]>([]);
    const current = ref<IManufactoringProduction>({} as IManufactoringProduction);
    const itemForm = ref<IManufactoringProductionForm>({} as IManufactoringProductionForm);
    const isLoading = ref<boolean>(false);

    // Fabrications déjà demandées par id et dont la production reste introuvable. Le prédicat
    // « manquante » de hydrateDispatchManufactorings porte sur l'HYDRATATION (mfp sans production),
    // pas sur la présence : sans cette mémoire, une fabrication dont la production est supprimée ou
    // hors périmètre est redemandée à chaque hydratation, indéfiniment. Purgée à chaque load(), qui
    // élargit la fenêtre et peut donc rendre la production résoluble.
    const unresolvedProductionMfpIds = new Set<number>();

    // Actions - Hydration
    /**
     * Hydrate ManufactoringProductions from API response
     *
     * @param preloadProductionStore Précharge le productionStore ENTIER avant de mapper. Utile au
     * chargement en masse (load), nuisible partout ailleurs : les chemins de résolution unitaire ou
     * par ids ne connaissent pas leur centre et passent donc `centerId: undefined`, ce qui faisait
     * partir `productionStore.load({ pageSize: 0, centerId: undefined })` — les productions de TOUS
     * les centres, puis hydrateProductions dessus, donc boarsStore complet et loadReformed.
     * Mesure du 31/08 sur STE : 40,5 s pour resoudre les fabrications de 8 lignes.
     * Ces chemins-la resolvent de toute facon leurs productions manquantes par ids juste apres.
     */
    async function hydrateManufactoringProductions(productions: IManufactoringProductionApiResponse[], centerId?: number, dateFrom?: Date, perfPage?: string, preloadProductionStore: boolean = true): Promise<IManufactoringProduction[]> {
        if (productions.length === 0) return [];
        const { start: perfStart, end: perfEnd } = usePerformanceTracker(perfPage);

        // 1. Get stores
        const productionStore = useProductionStore();

        // 2. Load in parallel if needed
        // Si le store est en cours de chargement, attendre qu'il finisse au lieu de skip
        const waitForStore = (store: { isLoading: boolean }) =>
            new Promise<void>(resolve => {
                if (!store.isLoading) return resolve();
                const interval = setInterval(() => {
                    if (!store.isLoading) { clearInterval(interval); resolve(); }
                }, 50);
            });

        const loadPromises: Promise<unknown>[] = [];
        const prodNeedsLoad =
            preloadProductionStore && (
            productionStore.datas.length === 0 ||
            (centerId != null && productionStore.loadedCenterId !== centerId) ||
            (dateFrom === undefined && productionStore.loadedDateFrom !== undefined) ||
            (dateFrom !== undefined && productionStore.loadedDateFrom !== undefined && productionStore.loadedDateFrom > dateFrom));
        if (prodNeedsLoad) {
            if (productionStore.isLoading) {
                loadPromises.push(waitForStore(productionStore));
            } else {
                loadPromises.push(productionStore.load({ page: 1, pageSize: 0, centerId, dateFrom }, perfPage));
            }
        }
        if (loadPromises.length > 0) {
            perfStart(' ↳ hydrateMfp : chargement productionStore');
            await Promise.all(loadPromises);
            perfEnd(' ↳ hydrateMfp : chargement productionStore', 'api');
        }

        // 3. Create Maps for O(1) lookups
        const productionMap = new Map(productionStore.datas.map(p => [p.id, p]));

        // Charger par id les productions manquantes. Le bloc ci-dessus ne recharge le
        // productionStore que s'il est vide ou moins large que la fenêtre demandée : une production
        // hors de cette fenêtre, d'un autre centre, ou apparue après le chargement, reste absente.
        // La manufactoringProduction était alors stockée sans sa production — définitivement, car
        // hydrateDispatchManufactorings ne vérifie que la PRÉSENCE de la manufactoringProduction,
        // pas son hydratation. Le suivi de fabrication affichait la ligne sans verrat, sans schéma
        // et sans date de prélèvement jusqu'au F5 (Mantis 0028321).
        // Même parade que les verrats et agents manquants de hydrateProductions.
        const missingProductionIds = [...new Set(
            productions
                .map(p => p.productionId)
                .filter((id): id is number => !!id && !productionMap.has(id))
        )];
        if (missingProductionIds.length > 0) {
            // Une seule requête `Ids=` au lieu d'un GET par production manquante.
            perfStart(' ↳ hydrateMfp : productions manquantes (Ids=)');
            const loaded = await productionStore.ensureLoadedByIds(missingProductionIds);
            perfEnd(' ↳ hydrateMfp : productions manquantes (Ids=)', 'api');
            loaded
                .filter((p): p is IProduction => p !== undefined)
                .forEach(p => productionMap.set(p.id, p));
        }

        // 4. Hydrate all entities in one pass
        const models: IManufactoringProduction[] = [];
        for (const production of productions) {
            const model = { ...production } as unknown as IManufactoringProduction;

            // Link references (O(1) via Map)
            model.production = productionMap.get(production.productionId) ?? model.production;

            models.push(model);
        }

        return models;
    }

    // Getters
    const getManufactoringById = computed(() => {
        return (id: number): IManufactoringProduction | undefined => {
            // Use productionId instead of production?.id to avoid undefined access
            return datas.value?.find((manufactoringProduction) => manufactoringProduction.productionId == id);
        };
    });

    const todayAndYesterdayManufactures = computed(() => {
        const now = new Date();
        const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const startOfYesterday = new Date(startOfToday);
        const startOfTomorrow = new Date(startOfToday);
        startOfYesterday.setDate(startOfYesterday.getDate() - 1);
        startOfTomorrow.setDate(startOfTomorrow.getDate() + 1);

        return datas.value.filter(m => {
            let itemDate = new Date(m.addingDate);
            return itemDate >= startOfYesterday && itemDate < startOfTomorrow;
        });
    });

    // State
    const loadedDateFrom = ref<Date | undefined>(undefined);

    // Actions
    async function load(filter: IManufactoringProductionFilter, perfPage?: string) {
        const { start, end } = usePerformanceTracker(perfPage);
        isLoading.value = true;

        // Un load élargit la fenêtre ou change de centre : une production jusque-là introuvable
        // peut le devenir, on redonne sa chance à chaque id mémorisé.
        unresolvedProductionMfpIds.clear();

        // Pré-chargement de productionStore en parallèle avec l'appel API principal.
        // hydrateManufactoringProductions() en a besoin : sans ça, il le charge séquentiellement après le GET.
        const productionStore = useProductionStore();
        const subStorePromises: Promise<unknown>[] = [];
        const prodNeedsLoad =
            productionStore.datas.length === 0 ||
            (filter.centerId != null && productionStore.loadedCenterId !== filter.centerId) ||
            (filter.dateFrom === undefined && productionStore.loadedDateFrom !== undefined) ||
            (filter.dateFrom !== undefined && productionStore.loadedDateFrom !== undefined && productionStore.loadedDateFrom > filter.dateFrom);
        if (!productionStore.isLoading && prodNeedsLoad) {
            subStorePromises.push(productionStore.load({ page: 1, pageSize: 0, centerId: filter.centerId, dateFrom: filter.dateFrom }));
        }

        start('GET /ManufactoringProduction (réseau)');
        const result = await ManufactoringProductionServices.getAll<IManufactoringProductionFilter, IManufactoringProductionApiResponse>(filter);
        end('GET /ManufactoringProduction (réseau)', 'api');

        if (subStorePromises.length > 0) {
            start('Attente productionStore');
            await Promise.all(subStorePromises);
            end('Attente productionStore', 'api');
        }

        datas.value = [];
        if (!result.error) {
            start('hydrateManufactoringProductions()');
            datas.value = await hydrateManufactoringProductions(result.data as IManufactoringProductionApiResponse[], filter.centerId, filter.dateFrom, perfPage);
            end('hydrateManufactoringProductions()', 'mapping');
        }
        isLoading.value = false;
        loadedDateFrom.value = filter.dateFrom;
        return result;
    }

    async function get(id: number) {
        const result = await ManufactoringProductionServices.getById<IManufactoringProductionApiResponse>(id);
        if (!result.error) {
            const [modelData] = await hydrateManufactoringProductions([result.data as IManufactoringProductionApiResponse], undefined, loadedDateFrom.value, undefined, false);

            const index = datas.value.findIndex((item) => item.id === id);
            if (index !== -1)
                datas.value[index] = modelData;

            current.value = modelData;
            itemForm.value = toForm(modelData);
        }
        return result;
    }

    async function getByProduction(id: number) {
        const result = await ManufactoringProductionServices.getByIdOther<IManufactoringProductionApiResponse>(id, "production");
        console.log("Result getByProduction", result);

        if (!result.error && (result.data as IManufactoringProductionApiResponse).id != 0) {
            const [modelData] = await hydrateManufactoringProductions([result.data as IManufactoringProductionApiResponse], undefined, loadedDateFrom.value, undefined, false);

            // Use productionId instead of production.id to avoid undefined access
            const index = datas.value.findIndex((item) => item.productionId === id);
            if (index !== -1) {
                datas.value[index] = modelData;
            } else {
                datas.value.push(modelData);
            }
            current.value = modelData;
            itemForm.value = toForm(modelData);
        }
        return result;
    }

    async function ensureLoadedById(id: number): Promise<IManufactoringProduction | undefined> {
        // Une entrée sans sa production n'est pas exploitable : on la reconstruit au lieu de la
        // renvoyer telle quelle, sinon l'appelant croit avoir réparé son trou (Mantis 0028321).
        const existing = datas.value.find(m => m.id === id);
        if (existing?.production) return existing;
        if (unresolvedProductionMfpIds.has(id)) return existing;

        const result = await ManufactoringProductionServices.getById<IManufactoringProductionApiResponse>(id);
        if (result.error || !result.data) return undefined;

        const apiData = result.data as IManufactoringProductionApiResponse;

        // La production associée (éventuellement d'un autre centre) est désormais résolue par
        // hydrateManufactoringProductions, qui charge celles qui manquent sans déplacer la
        // production courante — ce que faisait productionStore.get() appelé ici.
        const [model] = await hydrateManufactoringProductions([apiData], undefined, loadedDateFrom.value, undefined, false);
        const index = datas.value.findIndex(m => m.id === model.id);
        if (index >= 0) {
            datas.value[index] = model;
        } else {
            datas.value.push(model);
        }
        if (!model.production) unresolvedProductionMfpIds.add(id);
        return model;
    }

    /**
     * Variante en masse de {@link ensureLoadedById} : une seule requête `Ids=` pour toutes les
     * fabrications manquantes. Le suivi de fabrication en demandait une par dose hors fenêtre.
     *
     * Les ids restés sans production après hydratation sont mémorisés : le prédicat « manquante »
     * de hydrateDispatchManufactorings les redésignerait sinon à chaque passage.
     */
    async function ensureLoadedByIds(ids: number[], perfPage?: string): Promise<IManufactoringProduction[]> {
        const wanted = [...new Set(ids)].filter(id => !!id && !unresolvedProductionMfpIds.has(id));
        if (wanted.length === 0) return [];

        const resolved: IManufactoringProduction[] = [];
        const missing: number[] = [];
        for (const id of wanted) {
            const existing = datas.value.find(m => m.id === id);
            if (existing?.production) resolved.push(existing);
            else missing.push(id);
        }
        if (missing.length === 0) return resolved;

        const result = await ManufactoringProductionServices.getAll<IManufactoringProductionFilter, IManufactoringProductionApiResponse>({
            page: 1,
            pageSize: 0,
            ids: missing,
        });
        // Erreur réseau : rien n'est mémorisé, l'absence n'est pas prouvée.
        if (result.error || !result.data) return resolved;

        // preload = false : c'est ici que partait le chargement de toutes les productions de tous
        // les centres (40,5 s pour 8 lignes, mesure du 31/08). Les productions dont ces fabrications
        // ont besoin sont résolues par ids juste en dessous, dans hydrateManufactoringProductions.
        const models = await hydrateManufactoringProductions(
            result.data as IManufactoringProductionApiResponse[], undefined, loadedDateFrom.value, perfPage, false);

        const returned = new Map(models.map(m => [m.id, m]));
        for (const model of models) {
            const index = datas.value.findIndex(m => m.id === model.id);
            if (index >= 0) datas.value[index] = model;
            else datas.value.push(model);
            resolved.push(model);
        }
        for (const id of missing) {
            if (!returned.get(id)?.production) unresolvedProductionMfpIds.add(id);
        }
        return resolved;
    }

    async function save(maj: boolean = true, isPatch: boolean = false) {
        let result: ServiceOperationResult<IManufactoringProduction | IDataOperationResult<IManufactoringProduction>>;
        if (itemForm.value.id > 0) {
            result = await ManufactoringProductionServices.update(itemForm.value);
            if (!result.error) {
                const [modelData] = await hydrateManufactoringProductions([result.data as IManufactoringProductionApiResponse], undefined, loadedDateFrom.value, undefined, false);
                current.value = modelData;

                const index = datas.value.findIndex((item) => item.id === itemForm.value.id);
                if (index !== -1) {
                    datas.value[index] = current.value;
                } else {
                    const error = new DataOperationError();
                    error.REQUEST_ERROR = i18n.global.t("errors.itemNotFoundInStore");
                    return new ServiceOperationResult<IDataOperationResult<IManufactoringProduction>>(true, { errors: error });
                }
            }
        } else {
            result = await ManufactoringProductionServices.create(itemForm.value);
            if (!result.error) {
                const [modelData] = await hydrateManufactoringProductions([result.data as IManufactoringProductionApiResponse], undefined, loadedDateFrom.value, undefined, false);
                current.value = modelData;
            }
        }
        return result;
    }

    function toForm(item: IManufactoringProduction): IManufactoringProductionForm {
        const form = {} as IManufactoringProductionForm;
        Object.assign(form, item);
        form.productionId = item.productionId;
        itemForm.value = form;
        return form;
    }

    function newForm(lienId: number): IManufactoringProductionForm {
        itemForm.value = {
            id: 0,
            productionId: lienId,
            concentrationFinale: 0,
            volumeAmorce: 0,
            volumeFinal: 0,
            numberFabrication: 0,
            isHetero: false,
        } as IManufactoringProductionForm;
        return itemForm.value;
    }

    function newItem(lien: IProduction): IManufactoringProduction {
        current.value = {
            id: 0,
            production: lien,
            productionId: lien.id,
            concentrationFinale: 0,
            volumeAmorce: 0,
            volumeFinal: 0,
            numberFabrication: 0,
            isHetero: false,
        } as IManufactoringProduction;
        return current.value;
    }

    return {
        // State
        datas,
        current,
        itemForm,
        isLoading,
        loadedDateFrom,
        // Getters
        getManufactoringById,
        todayAndYesterdayManufactures,
        // Actions
        load,
        get,
        getByProduction,
        ensureLoadedById,
        ensureLoadedByIds,
        save,
        toForm,
        newForm,
        newItem,
        hydrateManufactoringProductions
    };
});

signalRManager.connection.on("MajManufactoringProduction", async (payload: IPayloadStore) => {
    const store = useManufactoringProductionStore();
    // Passer loadedDateFrom pour éviter de déclencher un rechargement sans filtre du productionStore
    const dateFrom = store.loadedDateFrom;
    switch (payload.action) {
        case "Create": {
            if (store.datas.findIndex((a) => a.id == payload.data?.id) == -1) {
                const [hydrated] = await store.hydrateManufactoringProductions([payload.data as IManufactoringProductionApiResponse], undefined, dateFrom, undefined, false);
                store.datas.push(hydrated);
            }
            break;
        }
        case "Update": {
            const idx = store.datas.findIndex((a) => a.id == payload.data?.id);
            if (idx > -1) {
                const [hydrated] = await store.hydrateManufactoringProductions([payload.data as IManufactoringProductionApiResponse], undefined, dateFrom, undefined, false);
                store.datas.splice(idx, 1, hydrated);
            }
            break;
        }
        case "Delete": {
            const idx = store.datas.findIndex((a) => a.id == payload.id);
            if (idx > -1) store.datas.splice(idx, 1);
            break;
        }
    }
});
